import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import api from '../services/api';
import Step1SelectPlan from '../components/wizard/Step1SelectPlan';
import Step2PaymentMode from '../components/wizard/Step2PaymentMode';
import Step3Additionals from '../components/wizard/Step3Additionals';

const PlanSelectionWizard = () => {
  const navigate = useNavigate();
  const { user, updateLicense, updateUser } = useAuth();
  const { isDark } = useTheme();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentMode, setPaymentMode] = useState(null);
  const [routerCount, setRouterCount] = useState(1);
  const [extraSellers, setExtraSellers] = useState(0);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadPricing();
    if (user?.router_count) {
      setRouterCount(user.router_count);
    }
  }, [user]);

  const loadPricing = async () => {
    try {
      const response = await api.get('/api/license-pricing/public');
      setPricing(response.data);
    } catch (error) {
      console.error('Error cargando precios:', error);
    }
  };

  const calculateTotal = () => {
    if (!pricing || selectedPlan === 'free') return 0;
    
    const planPricing = pricing[selectedPlan];
    if (!planPricing) return 0;
    
    const modeMap = {
      'monthly': 'monthly',
      'semiannual': 'semiannual',
      'annual': 'annual',
      'lifetime': 'lifetime'
    };
    
    const basePrice = planPricing[modeMap[paymentMode]] || 0;
    
    // Routers adicionales
    const extraRouters = Math.max(0, routerCount - 1);
    const routerCost = extraRouters * (pricing.extra_router_cost || 0);
    
    // Vendedores adicionales
    const extraSellersCost = Math.max(0, extraSellers) * (pricing.extra_seller_cost || 0);
    
    return basePrice + routerCost + extraSellersCost;
  };

  const handleNext = () => {
    if (currentStep === 1 && selectedPlan) {
      setCurrentStep(2);
    } else if (currentStep === 2 && paymentMode) {
      setCurrentStep(3);
    } else if (currentStep === 3) {
      handleFinish();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = async () => {
    if (!selectedPlan || !paymentMode) {
      alert('Por favor completa todos los pasos');
      return;
    }

    setLoading(true);
    try {
      // Nuevo endpoint unificado que crea license básica
      const response = await api.post(`/api/users/${user.id}/complete-onboarding`, {
        plan_type: selectedPlan,
        payment_mode: paymentMode,
        total_routers: routerCount,
        extra_sellers: extraSellers,
        calculated_price: calculateTotal()
      });

      // Actualizar context con la license básica (sin VPN aún)
      if (response.data.license) {
        updateLicense(response.data.license);
        
        // Actualizar user con license_id para evitar race condition
        const updatedUser = { ...user, license_id: response.data.license.id };
        updateUser(updatedUser);
      }

      // Navegar a página de bienvenida
      navigate('/welcome', { replace: true });
    } catch (error) {
      console.error('Error completando onboarding:', error);
      alert('Error al completar registro: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const canGoNext = () => {
    if (currentStep === 1) return selectedPlan !== null;
    if (currentStep === 2) return paymentMode !== null;
    if (currentStep === 3) return true;
    return false;
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDark 
        ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900' 
        : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'
    }`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        
        {/* Progress indicator */}
        <div className="mb-10">
          <div className="flex justify-center mb-4">
            <span className="px-4 py-2 bg-blue-600 text-white rounded-full text-sm font-bold">
              PASO {currentStep} DE 3
            </span>
          </div>
          
          {/* Progress bar */}
          <div className="flex items-center justify-center gap-2">
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all ${
                  step < currentStep 
                    ? 'bg-green-600 text-white' 
                    : step === currentStep
                    ? 'bg-blue-600 text-white scale-110'
                    : isDark
                    ? 'bg-gray-700 text-gray-400'
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {step < currentStep ? '✓' : step}
                </div>
                {step < 3 && (
                  <div className={`w-16 h-1 ${
                    step < currentStep ? 'bg-green-600' : isDark ? 'bg-gray-700' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
          
          <div className="flex justify-center gap-8 mt-4">
            <span className={`text-sm font-medium ${
              currentStep === 1 ? 'text-blue-600' : currentStep > 1 ? 'text-green-600' : isDark ? 'text-gray-500' : 'text-gray-400'
            }`}>
              Elegir Plan
            </span>
            <span className={`text-sm font-medium ${
              currentStep === 2 ? 'text-blue-600' : currentStep > 2 ? 'text-green-600' : isDark ? 'text-gray-500' : 'text-gray-400'
            }`}>
              Modalidad
            </span>
            <span className={`text-sm font-medium ${
              currentStep === 3 ? 'text-blue-600' : isDark ? 'text-gray-500' : 'text-gray-400'
            }`}>
              Adicionales
            </span>
          </div>
        </div>

        {/* Step content */}
        <div className="mb-8">
          {currentStep === 1 && (
            <Step1SelectPlan
              selectedPlan={selectedPlan}
              onSelectPlan={setSelectedPlan}
            />
          )}
          
          {currentStep === 2 && (
            <Step2PaymentMode
              selectedPlan={selectedPlan}
              paymentMode={paymentMode}
              onSelectMode={setPaymentMode}
              pricing={pricing}
            />
          )}
          
          {currentStep === 3 && (
            <Step3Additionals
              selectedPlan={selectedPlan}
              paymentMode={paymentMode}
              routerCount={routerCount}
              extraSellers={extraSellers}
              onChangeExtraSellers={setExtraSellers}
              pricing={pricing}
            />
          )}
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between items-center">
          <button
            onClick={handleBack}
            disabled={currentStep === 1}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
              currentStep === 1
                ? 'opacity-0 cursor-not-allowed'
                : isDark
                ? 'bg-gray-700 text-white hover:bg-gray-600'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <ArrowLeft className="w-5 h-5" />
            Atrás
          </button>

          <button
            onClick={handleNext}
            disabled={!canGoNext() || loading}
            className={`flex items-center gap-2 px-8 py-4 rounded-lg font-bold text-lg transition-all transform ${
              canGoNext() && !loading
                ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white hover:scale-105 shadow-xl'
                : 'bg-gray-400 text-gray-200 cursor-not-allowed'
            }`}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Procesando...
              </>
            ) : currentStep === 3 ? (
              <>
                Finalizar
                <ArrowRight className="w-5 h-5" />
              </>
            ) : (
              <>
                Siguiente
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlanSelectionWizard;
